import React, { useState, useRef, useEffect } from 'react';
import moment from 'moment';
import './DateRangePicker.css';

const DateRangePicker = ({ onSelected }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState(moment().startOf('week'));
  const [endDate, setEndDate] = useState(moment().endOf('week'));
  const [hoveredDate, setHoveredDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(moment());
  const [inputValue, setInputValue] = useState('');
  const [activeShortcut, setActiveShortcut] = useState('this-week');
  const [isLeftAligned, setIsLeftAligned] = useState(true);
  const calendarRef = useRef(null);

  const handleClickOutside = (event) => {
    if (calendarRef.current && !calendarRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (calendarRef.current) {
        const rect = calendarRef.current.getBoundingClientRect();
        if (rect.right + 220 > window.innerWidth) {
          setIsLeftAligned(false);
        } else {
          setIsLeftAligned(true);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleCalendar = () => {
    setIsOpen(!isOpen);
  };

  const handleDateClick = (date) => {
    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
      setHoveredDate(null);
    } else if (date.isBefore(startDate)) {
      setStartDate(date);
    } else {
      setEndDate(date);
      setInputValue(`${startDate.format('MM/DD/YYYY')} - ${date.format('MM/DD/YYYY')}`);
    }
  };

  const handleDateMouseEnter = (date) => {
    if (startDate && !endDate) {
      setHoveredDate(date);
    }
  };

  const changeMonth = (direction) => {
    setCurrentMonth(currentMonth.clone().add(direction, 'month'));
  };

  const renderCalendarHeader = () => {
    return (
      <div className="calendar-header">
        <button className='rdrNextPrevButton rdrPprevButton' onClick={() => changeMonth(-1)}><i></i></button>
        <span className='rdrMonthYear'>{currentMonth.format('MMMM, YYYY')}</span>
        <button className='rdrNextPrevButton rdrNextButton' onClick={() => changeMonth(1)}><i></i></button>
      </div>
    );
  };

  const renderDaysOfWeek = () => {
    const daysOfWeek = moment.weekdaysShort();
    return (
      <div className="calendar-days">
        {daysOfWeek.map(day => (
          <div key={day} className="calendar-day">
            {day}
          </div>
        ))}
      </div>
    );
  };

  const renderDays = () => {
    const days = generateCalendar();

    return (
      <div className="calendar-grid">
        {days.map((day) => {
          const isStartSelected = day.isSame(startDate, 'day');
          const isEndSelected = day.isSame(endDate, 'day');
          const isInRange = day.isBetween(startDate, endDate || hoveredDate, 'day');
          const isNotCurrentDays = day.month() !== currentMonth.month() && !isStartSelected && !isEndSelected && !isInRange
          return (
            <div
              key={day.format('DD-MM-YYYY')}
              className={`day ${isStartSelected && 'selected'} ${isEndSelected && 'selected'} ${isInRange && 'in-range'} ${isNotCurrentDays && 'day-faded'}`}
              onClick={() => handleDateClick(day)}
              onMouseEnter={() => handleDateMouseEnter(day)}
            >
              {day.date()}
            </div>
          )
        })}
      </div>
    );
  };

  const generateCalendar = () => {
    const startOfMonth = currentMonth.clone().startOf('month').startOf('week');
    const endOfMonth = currentMonth.clone().endOf('month').endOf('week');
    const days = [];
    for (let date = startOfMonth; date.isBefore(endOfMonth); date.add(1, 'days')) {
      days.push(date.clone());
    }
    return days;
  };

  const handleShortcut = (type) => {
    let start, end;
    const today = moment();

    switch (type) {
      case 'today':
        start = end = today;
        break;
      case 'this-week':
        start = today.clone().startOf('week');
        end = today.clone().endOf('week');
        break;
      case 'last-week':
        start = today.clone().subtract(1, 'week').startOf('week');
        end = today.clone().subtract(1, 'week').endOf('week');
        break;
      case 'this-month':
        start = today.clone().startOf('month');
        end = today.clone().endOf('month');
        break;
      default:
        return;
    }
    setActiveShortcut(type);
    setStartDate(start);
    setEndDate(end);
    if (end.isSameOrBefore(currentMonth)) {
      setCurrentMonth(end);
    }
    if (end.isSameOrAfter(currentMonth)) {
      setCurrentMonth(end);
    }
  };

  const handleSubmit = () => {
    if (!inputValue) {
      setInputValue(`${startDate.format('MM/DD/YYYY')} - ${endDate.format('MM/DD/YYYY')}`);
    }
    setIsOpen(false);
    onSelected(startDate, endDate)
  }

  const handleCancel = () => {
    setIsOpen(false);
    setInputValue('');
    onSelected(null, null)
  }

  const renderCalendar = () => {
    return (
      <div className={`calendar ${isLeftAligned ? 'left-side' : 'right-side'}`} ref={calendarRef}>
        <div className='calendar-shortcuts'>
          <button className={`btn btn-sm ${activeShortcut === 'today' ? 'selected' : ''}`} onClick={() => handleShortcut('today')}>Today</button>
          <button className={`btn btn-sm ${activeShortcut === 'this-week' ? 'selected' : ''}`} onClick={() => handleShortcut('this-week')}>This Week</button>
          <button className={`btn btn-sm ${activeShortcut === 'last-week' ? 'selected' : ''}`} onClick={() => handleShortcut('last-week')}>Last Week</button>
          <button className={`btn btn-sm ${activeShortcut === 'this-month' ? 'selected' : ''}`} onClick={() => handleShortcut('this-month')}>This Month</button>
        </div>
        <div className='calendar-pages'>
          {renderCalendarHeader()}
          {renderDaysOfWeek()}
          {renderDays()}
          <div className='calendar-actions-buttons'>
            <button className={`btn btn-xs d-flex align-items-center btn-outline-success`} onClick={handleSubmit}>Apply</button>
            <button className={`btn btn-xs d-flex align-items-center btn-outline-danger`} onClick={handleCancel}>Cancel</button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="date-range-picker">
      <div className="close-icon">
        <input
          type="text"
          value={inputValue}
          onClick={toggleCalendar}
          readOnly
          disabled={isOpen}
          placeholder="Select Date Range"
        />
        {inputValue && !isOpen && <span onClick={handleCancel}>&times;</span>}
      </div>
      {isOpen && renderCalendar()}
    </div>
  );
};

export default DateRangePicker;
